import { useState } from "react";
import { Box, Button, IconButton, Popover, Stack, TextField, ToggleButton, Tooltip, Typography } from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import { MAX_PLAYERS_PER_ROW, parsePlayersPerRow } from "../utils/playerGridLayout";

type Props = { playersPerRow: number; onChange: (columns: number) => void };

const GridPreview = ({ columns }: { columns: number }) => (
  <Box aria-hidden sx={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 0.4, width: 48, height: 28 }}>
    {Array.from({ length: columns * 2 }, (_, index) => (
      <Box key={index} sx={{ border: "1px solid currentColor", borderRadius: "2px" }} />
    ))}
  </Box>
);

export default function PlayerLayoutControl({ playersPerRow, onChange }: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [custom, setCustom] = useState(false);
  const [draft, setDraft] = useState(String(playersPerRow));
  const parsed = parsePlayersPerRow(draft);
  const choose = (value: number) => { onChange(value); setAnchor(null); };

  return (
    <>
      <Tooltip title="Layout">
        <IconButton
          aria-label="Layout"
          aria-haspopup="dialog"
          aria-expanded={Boolean(anchor)}
          aria-controls={anchor ? "player-layout-popup" : undefined}
          color={anchor ? "primary" : "default"}
          onClick={(event) => {
            setDraft(String(playersPerRow));
            setCustom(playersPerRow > 3);
            setAnchor(event.currentTarget);
          }}
        >
          <GridViewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { id: "player-layout-popup", role: "dialog", "aria-label": "Player layout", sx: { p: 2, width: 300 } } }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Players per row</Typography>
        <Stack direction="row" spacing={1}>
          {[1, 2, 3].map((columns) => (
            <ToggleButton key={columns} value={columns} selected={!custom && playersPerRow === columns}
              onClick={() => choose(columns)} aria-label={`${columns} ${columns === 1 ? "player" : "players"} per row`}
              sx={{ flex: 1, flexDirection: "column", gap: 0.75, textTransform: "none" }}>
              <GridPreview columns={columns} />
              {columns}
            </ToggleButton>
          ))}
        </Stack>
        <ToggleButton value="custom" selected={custom} onClick={() => setCustom(true)}
          size="small" sx={{ mt: 1, width: "100%", textTransform: "none" }}>Custom</ToggleButton>
        {custom && (
          <Box component="form" onSubmit={(event) => { event.preventDefault(); if (parsed) choose(parsed); }} sx={{ mt: 2 }}>
            <TextField autoFocus fullWidth size="small" type="number" label="Players per row" value={draft}
              onChange={(event) => setDraft(event.target.value)} error={parsed === null}
              helperText={parsed === null ? `Enter a whole number from 1 to ${MAX_PLAYERS_PER_ROW}` : undefined}
              slotProps={{ htmlInput: { min: 1, max: MAX_PLAYERS_PER_ROW, step: 1 } }} />
            <Button type="submit" variant="contained" size="small" disabled={parsed === null} sx={{ mt: 1 }}>Apply</Button>
          </Box>
        )}
      </Popover>
    </>
  );
}
